import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { SalesRepData } from '../services/salesRepAPI';

export type RankingMetric = 'faturamento' | 'pares';

interface TopSalesRepChartProps {
    data: SalesRepData[];
    metric: RankingMetric;
}

const TopSalesRepChart: React.FC<TopSalesRepChartProps> = ({ data, metric }) => {
    
    const formatValue = (value: number) => {
        if (metric === 'faturamento') {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                notation: 'compact',
                compactDisplay: 'short'
            }).format(value);
        }
        return new Intl.NumberFormat('pt-BR').format(value);
    };

    const labelFormatter = (value: any) => {
        const numValue = Number(value);
        if (isNaN(numValue)) return '';
        return formatValue(numValue);
    };

    const sortedData = [...data].sort((a, b) => {
        if (metric === 'pares') {
            return Number(b.totalPares) - Number(a.totalPares);
        }
        return Number(b.totalVenda) - Number(a.totalVenda);
    }).slice(0, 8);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            // Acessa os dados originais da barra onde passamos o mouse
            const originalData = payload[0].payload;

            // LÓGICA NOVA: Usa o nome completo se existir (Representantes), senão usa o label do eixo (Vendedores)
            const tituloTooltip = originalData.nomeCompleto || label;

            return (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '12px',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-base, 8px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    {/* Exibe o Nome Completo aqui */}
                    <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                        {tituloTooltip}
                    </p>
                    
                    {metric === 'faturamento' ? (
                        <>
                            <p style={{ color: 'var(--text-primary)', margin: '4px 0' }}>
                                Faturamento: {formatValue(payload[0].value)}
                            </p>
                            <p style={{ color: '#b91c1c', margin: '4px 0' }}>
                                Margem: {formatValue(payload[1].value)}
                            </p>
                        </>
                    ) : (
                        <p style={{ color: 'var(--text-primary)', margin: '4px 0' }}>
                            Pares Vendidos: {formatValue(payload[0].value)}
                        </p>
                    )}

                    {/* LINHA RESTAURADA AQUI */}
                    <p style={{ color: '#666', margin: '4px 0', marginTop: '8px', fontSize: '0.9em' }}>
                        Pedidos: {originalData.numeroPedidos}
                    </p>
                </div>
            );
        }
        return null;
    };

    const CustomLegend = (props: any) => {
        const { payload } = props;
        return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', paddingTop: '10px' }}>
                {payload.map((entry: any, index: number) => (
                    <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: entry.color, 
                            borderRadius: '3px', // AQUI ESTÁ O ARREDONDAMENTO
                            marginRight: '8px' 
                        }}></div>
                        <span style={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    };
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={sortedData}
                layout="vertical"
                margin={{ top: 20, right: 80, left: 20, bottom: 5 }}
                onMouseDown={(_, event) => event.preventDefault()}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                    type="number" 
                    tickFormatter={formatValue}
                    tick={{ fontSize: 12 }}
                />
                <YAxis 
                    type="category" 
                    dataKey="nomeVendedor"
                    tick={{ fontSize: 12 }}
                    width={90}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                
                {metric === 'faturamento' ? (
                    <>
                        <Bar 
                            dataKey="totalVenda" 
                            name="Faturamento"
                            fill="var(--theme-accent, #1e293b)"
                            radius={[0, 0, 0, 0]}
                            barSize={22}
                        >
                            <LabelList 
                                dataKey="totalVenda" 
                                position="right" 
                                formatter={labelFormatter}
                                style={{ fontSize: '11px', fill: '#333', fontWeight: 'bold' }}
                            />
                        </Bar>
                        <Bar 
                            dataKey="totalMargem" 
                            name="Margem"
                            fill="var(--theme-primary, #b91c1c)"
                            radius={[0, 0, 0, 0]}
                            barSize={22}
                        />
                    </>
                ) : (
                    <Bar 
                        dataKey="totalPares" 
                        name="Pares Vendidos"
                        fill="var(--theme-accent, #1e293b)"
                        radius={[0, 0, 0, 0]}
                        barSize={22}
                    >
                        <LabelList 
                            dataKey="totalPares" 
                            position="right" 
                            formatter={labelFormatter}
                            style={{ fontSize: '11px', fill: '#333', fontWeight: 'bold' }}
                        />
                    </Bar>
                )}
            </BarChart>
        </ResponsiveContainer>
    );
};

export default TopSalesRepChart;